import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { JsonSyntaxHighlightPipe } from '../pipes/json-syntax-highlight.pipe';

@Component({
  selector: 'app-auth-panel',
  standalone: true,
  imports: [CommonModule, JsonSyntaxHighlightPipe],
  template: `
    <div class="border border-gray-200 rounded-lg overflow-hidden">
      <!-- Header with method badge -->
      <div class="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200 flex-wrap">
        <span class="px-2.5 py-1 text-xs font-bold rounded uppercase tracking-wide bg-blue-100 text-blue-700">POST</span>
        <code class="text-sm font-mono text-gray-800">/partner/oauth/token</code>
        <span *ngIf="hasToken" class="ml-auto flex items-center gap-1.5">
          <span class="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
          <span class="text-xs text-emerald-600 font-medium">Authenticated</span>
        </span>
      </div>

      <div class="p-5 space-y-5">
        <div>
          <h4 class="text-lg font-semibold text-gray-900 mb-1">Generate Access Token</h4>
          <p class="text-sm text-gray-600 leading-relaxed">
            Exchange your <code class="bg-gray-100 px-1 py-0.5 rounded text-xs text-indigo-700 font-mono">app_key</code>
            and <code class="bg-gray-100 px-1 py-0.5 rounded text-xs text-indigo-700 font-mono">app_secret</code>
            for a Bearer JWT token. The token is valid for <strong>60 minutes</strong> and must be included in the
            <code class="bg-gray-100 px-1 py-0.5 rounded text-xs text-indigo-700 font-mono">Authorization</code>
            header of all subsequent API requests.
          </p>
        </div>

        <!-- Request body docs -->
        <div>
          <h5 class="text-sm font-semibold text-gray-700 mb-2">Request Body</h5>
          <div class="overflow-x-auto">
            <table class="w-full text-sm border border-gray-200 rounded-lg">
              <thead>
                <tr class="bg-gray-50">
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Field</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Required</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr class="border-t border-gray-100">
                  <td class="px-3 py-2 font-mono text-xs text-indigo-700">app_key</td>
                  <td class="px-3 py-2 text-gray-500 text-xs">string</td>
                  <td class="px-3 py-2 text-red-600 font-medium text-xs">Yes</td>
                  <td class="px-3 py-2 text-gray-600 text-xs">Your application key provided during partner registration.</td>
                </tr>
                <tr class="border-t border-gray-100">
                  <td class="px-3 py-2 font-mono text-xs text-indigo-700">app_secret</td>
                  <td class="px-3 py-2 text-gray-500 text-xs">string</td>
                  <td class="px-3 py-2 text-red-600 font-medium text-xs">Yes</td>
                  <td class="px-3 py-2 text-gray-600 text-xs">Your application secret provided during partner registration.</td>
                </tr>
                <tr class="border-t border-gray-100">
                  <td class="px-3 py-2 font-mono text-xs text-indigo-700">grant_type</td>
                  <td class="px-3 py-2 text-gray-500 text-xs">string</td>
                  <td class="px-3 py-2 text-red-600 font-medium text-xs">Yes</td>
                  <td class="px-3 py-2 text-gray-600 text-xs">Must be exactly "client_credentials".</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Response example -->
        <div>
          <h5 class="text-sm font-semibold text-gray-700 mb-2">
            Response <span class="text-emerald-600 font-normal ml-1">200 OK</span>
          </h5>
          <pre class="code-block text-xs"><code [innerHTML]="responseExample | jsonHighlight"></code></pre>
        </div>

        <!-- Error codes -->
        <div>
          <h5 class="text-sm font-semibold text-gray-700 mb-2">Error Responses</h5>
          <div class="overflow-x-auto">
            <table class="w-full text-sm border border-gray-200 rounded-lg">
              <thead>
                <tr class="bg-gray-50">
                  <th class="px-3 py-2 text-left font-medium text-gray-600 w-24">Status</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr class="border-t border-gray-100">
                  <td class="px-3 py-2"><span class="px-2 py-0.5 text-xs font-bold rounded bg-orange-100 text-orange-700">400</span></td>
                  <td class="px-3 py-2 text-gray-600 text-xs">Invalid grant_type — must be "client_credentials".</td>
                </tr>
                <tr class="border-t border-gray-100">
                  <td class="px-3 py-2"><span class="px-2 py-0.5 text-xs font-bold rounded bg-orange-100 text-orange-700">401</span></td>
                  <td class="px-3 py-2 text-gray-600 text-xs">Invalid app_key or app_secret.</td>
                </tr>
                <tr class="border-t border-gray-100">
                  <td class="px-3 py-2"><span class="px-2 py-0.5 text-xs font-bold rounded bg-orange-100 text-orange-700">403</span></td>
                  <td class="px-3 py-2 text-gray-600 text-xs">Application is disabled. Contact the clinic administrator.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- cURL -->
        <div>
          <h5 class="text-sm font-semibold text-gray-700 mb-2">cURL</h5>
          <pre class="code-block text-xs whitespace-pre-wrap"><code>{{ curlExample }}</code></pre>
        </div>

        <!-- Try It button -->
        <div class="border-t border-gray-200 pt-4">
          <button type="button" (click)="tryItClick.emit()"
            class="flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors">
            Try It
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`:host
  display: block
.code-block
  background: #111827
  color: #e5e7eb
  font-family: 'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Courier New', monospace
  font-size: 0.8125rem
  line-height: 1.6
  border-radius: 0.5rem
  padding: 1rem
  overflow-x: auto
  margin: 0
:host ::ng-deep .jh-key
  color: #c4b5fd
:host ::ng-deep .jh-string
  color: #6ee7b7
:host ::ng-deep .jh-number
  color: #93c5fd
:host ::ng-deep .jh-boolean
  color: #fcd34d
:host ::ng-deep .jh-null
  color: #9ca3af
`]
})
export class AuthPanelComponent {
  @Input() hasToken = false;
  @Output() tryItClick = new EventEmitter<void>();

  responseExample = JSON.stringify({
    access_token: 'eyJhbGciOiJIUzUxMiJ9...',
    token_type: 'Bearer',
    expires_in: 3600,
    scopes: 'sales:read,patients:read,anamnesis:read,treatment_plans:read'
  }, null, 2);

  curlExample = `curl -X POST https://server.westetics.com.br/partner/oauth/token \\
  -H "Content-Type: application/json" \\
  -d '{
    "app_key": "your-app-key",
    "app_secret": "your-app-secret",
    "grant_type": "client_credentials"
  }'`;
}
